import {
  QueryKey,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { queryStrategies, QueryStrategyName } from "./query-strategies";

type CreateQueryOptions<
  TQueryFnData,
  TError,
  TData,
  TQueryKey extends QueryKey,
> = Omit<
  UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
  "queryKey" | "queryFn"
>;

export const createQuery = <
  TQueryFnData,
  TError = Error,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  queryKey: TQueryKey,
  queryFn: () => Promise<TQueryFnData>,
  strategy: QueryStrategyName,
  options?: CreateQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
): UseQueryOptions<TQueryFnData, TError, TData, TQueryKey> => {
  return {
    queryKey,
    queryFn,
    ...queryStrategies[strategy],
    ...options,
  };
};

type QueryHookOptions<
  TQueryFnData,
  TError,
  TData,
  TQueryKey extends QueryKey,
> = CreateQueryOptions<TQueryFnData, TError, TData, TQueryKey>;

export const useStrategyQuery = <
  TQueryFnData,
  TError = Error,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  queryKey: TQueryKey,
  queryFn: () => Promise<TQueryFnData>,
  strategy: QueryStrategyName,
  options?: QueryHookOptions<TQueryFnData, TError, TData, TQueryKey>,
): UseQueryResult<TData, TError> => {
  return useQuery(createQuery(queryKey, queryFn, strategy, options));
};

export const useRealtimeQuery = <
  TQueryFnData,
  TError = Error,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  queryKey: TQueryKey,
  queryFn: () => Promise<TQueryFnData>,
  options?: QueryHookOptions<TQueryFnData, TError, TData, TQueryKey>,
) => {
  return useStrategyQuery(queryKey, queryFn, "realtime", options);
};

export const useSemiDynamicQuery = <
  TQueryFnData,
  TError = Error,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  queryKey: TQueryKey,
  queryFn: () => Promise<TQueryFnData>,
  options?: QueryHookOptions<TQueryFnData, TError, TData, TQueryKey>,
) => {
  return useStrategyQuery(queryKey, queryFn, "semiDynamic", options);
};

export const useStaticQuery = <
  TQueryFnData,
  TError = Error,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  queryKey: TQueryKey,
  queryFn: () => Promise<TQueryFnData>,
  options?: QueryHookOptions<TQueryFnData, TError, TData, TQueryKey>,
) => {
  return useStrategyQuery(queryKey, queryFn, "static", options);
};
